import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined, UserOutlined, FileOutlined, SearchOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Lead {
  id: string;
  type: 'INDIVIDUAL' | 'COMPANY';
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  address?: string;
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'REJECTED';
  source?: string;
  claimType?: string;
  claimDescription?: string;
  dateOfLoss?: string;
  estimatedValue?: number;
  insurerName?: string;
  policyNumber?: string;
  lastContactDate?: string;
  nextFollowUpDate?: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  assignedTo?: string;
  notes?: string;
  convertedToClientId?: string;
  convertedToClaimId?: string;
  convertedAt?: string;
  created_at: string;
  updated_at: string;
  assignee?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  convertedClient?: {
    id: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
  };
  convertedClaim?: {
    id: string;
    file_number: string;
    claimNumber: string;
  };
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface Metrics {
  totalLeads: number;
  newLeads: number;
  convertedLeads: number;
  conversionRate: number;
}

const LeadsManagement: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [convertingLead, setConvertingLead] = useState<Lead | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalLeads: 0,
    newLeads: 0,
    convertedLeads: 0,
    conversionRate: 0
  });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: Lead[] }>('/leads');
      if (response.data.success) {
        let filteredLeads = response.data.data;
        
        // Apply filters
        if (searchText) {
          filteredLeads = filteredLeads.filter(lead => 
            (lead.firstName?.toLowerCase().includes(searchText.toLowerCase())) ||
            (lead.lastName?.toLowerCase().includes(searchText.toLowerCase())) ||
            (lead.companyName?.toLowerCase().includes(searchText.toLowerCase())) ||
            (lead.email?.toLowerCase().includes(searchText.toLowerCase())) ||
            (lead.phone?.includes(searchText))
          );
        }
        
        if (statusFilter) {
          filteredLeads = filteredLeads.filter(lead => lead.status === statusFilter);
        }
        
        if (sourceFilter) {
          filteredLeads = filteredLeads.filter(lead => lead.source === sourceFilter);
        }
        
        setLeads(filteredLeads);
        calculateMetrics(filteredLeads);
      } else {
        message.error('Failed to fetch leads');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      message.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [searchText, statusFilter, sourceFilter]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get<{ success: boolean; data: User[] }>('/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const calculateMetrics = (data: Lead[]) => {
    const totalLeads = data.length;
    const newLeads = data.filter(lead => lead.status === 'NEW').length;
    const convertedLeads = data.filter(lead => lead.status === 'CONVERTED').length;
    const conversionRate = totalLeads > 0 ? (convertedLeads / totalLeads) * 100 : 0;
    
    setMetrics({
      totalLeads,
      newLeads,
      convertedLeads,
      conversionRate
    });
  };

  useEffect(() => {
    fetchLeads();
    fetchUsers();
  }, [fetchLeads, fetchUsers]);

  const handleEdit = (lead: Lead) => {
    setEditingLead(lead);
    setIsModalVisible(true);
    form.setFieldsValue({
      ...lead,
      dateOfLoss: lead.dateOfLoss ? dayjs(lead.dateOfLoss) : null,
      lastContactDate: lead.lastContactDate ? dayjs(lead.lastContactDate) : null,
      nextFollowUpDate: lead.nextFollowUpDate ? dayjs(lead.nextFollowUpDate) : null,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/leads/${id}`);
      message.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      console.error('Error deleting lead:', error);
      message.error('Failed to delete lead');
    }
  };

  const handleCreate = () => {
    setEditingLead(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        dateOfLoss: values.dateOfLoss?.format('YYYY-MM-DD'),
        lastContactDate: values.lastContactDate?.format('YYYY-MM-DD'),
        nextFollowUpDate: values.nextFollowUpDate?.format('YYYY-MM-DD'),
      };

      if (editingLead) {
        await api.put(`/leads/${editingLead.id}`, formattedValues);
        message.success('Lead updated successfully');
      } else {
        await api.post('/leads', formattedValues);
        message.success('Lead created successfully');
      }
      setIsModalVisible(false);
      fetchLeads();
    } catch (error) {
      console.error('Error saving lead:', error);
      message.error('Failed to save lead');
    }
  };

  const handleConvert = (lead: Lead) => {
    setConvertingLead(lead);
    setIsConvertModalVisible(true);
  };

  const handleConvertConfirm = async () => {
    if (!convertingLead) return;
    
    try {
      const response = await api.post(`/leads/${convertingLead.id}/convert`);
      if (response.data.success) {
        message.success('Lead converted successfully!');
        setIsConvertModalVisible(false);
        fetchLeads();
      } else {
        message.error('Failed to convert lead');
      }
    } catch (error) {
      console.error('Error converting lead:', error);
      message.error('Failed to convert lead');
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      NEW: 'blue',
      CONTACTED: 'orange',
      QUALIFIED: 'purple',
      CONVERTED: 'green',
      REJECTED: 'red',
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      LOW: 'green',
      MEDIUM: 'orange',
      HIGH: 'red',
    };
    return colors[priority as keyof typeof colors] || 'default';
  };

  const columns: ColumnsType<Lead> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <div>
          <div 
            style={{ 
              cursor: 'pointer', 
              color: '#1890ff',
              textDecoration: 'underline',
              fontWeight: 500
            }}
            onClick={() => navigate(`/leads/${record.id}`)}
          >
            {record.type === 'COMPANY' 
              ? record.companyName 
              : `${record.firstName} ${record.lastName}`
            }
          </div>
          <small style={{ color: '#888' }}>{record.type}</small>
        </div>
      ),
      sorter: true,
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <small style={{ color: '#888' }}>{record.phone}</small>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status}
        </Tag>
      ),
      filters: [
        { text: 'New', value: 'NEW' },
        { text: 'Contacted', value: 'CONTACTED' },
        { text: 'Qualified', value: 'QUALIFIED' },
        { text: 'Converted', value: 'CONVERTED' },
        { text: 'Rejected', value: 'REJECTED' },
      ],
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => (
        <Tag color={getPriorityColor(priority)}>
          {priority}
        </Tag>
      ),
      sorter: true,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      sorter: true,
    },
    {
      title: 'Assigned To',
      key: 'assignee',
      render: (_, record) => (
        record.assignee 
          ? `${record.assignee.firstName} ${record.assignee.lastName}`
          : '-'
      ),
    },
    {
      title: 'Estimated Value',
      dataIndex: 'estimatedValue',
      key: 'estimatedValue',
      render: (value: number) => value ? `$${value.toLocaleString()}` : '-',
      sorter: true,
    },
    {
      title: 'Next Follow-up',
      dataIndex: 'nextFollowUpDate',
      key: 'nextFollowUpDate',
      render: (date: string) => date ? dayjs(date).format('MM/DD/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
            size="small"
          />
          {record.status !== 'CONVERTED' && (
            <Button 
              icon={<SwapOutlined />} 
              onClick={() => handleConvert(record)}
              size="small"
              type="primary"
            >
              Convert
            </Button>
          )}
          {record.status === 'CONVERTED' && record.convertedClient && (
            <Tag color="green">
              <UserOutlined /> Client Created
            </Tag>
          )}
          <Button 
            icon={<DeleteOutlined />} 
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Metrics Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Leads"
              value={metrics.totalLeads}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="New Leads"
              value={metrics.newLeads}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Converted"
              value={metrics.convertedLeads}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Conversion Rate"
              value={metrics.conversionRate}
              precision={1}
              suffix="%"
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Input
              placeholder="Search leads..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="NEW">New</Option>
              <Option value="CONTACTED">Contacted</Option>
              <Option value="QUALIFIED">Qualified</Option>
              <Option value="CONVERTED">Converted</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Filter by Source"
              value={sourceFilter}
              onChange={setSourceFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="Website">Website</Option>
              <Option value="Referral">Referral</Option>
              <Option value="Advertisement">Advertisement</Option>
              <Option value="Social Media">Social Media</Option>
              <Option value="Cold Call">Cold Call</Option>
            </Select>
          </Col>
          <Col span={8}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Add New Lead
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={(pagination, filters, sorter) => {
            setPagination(pagination);
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingLead ? 'Edit Lead' : 'Create New Lead'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select>
                  <Option value="INDIVIDUAL">Individual</Option>
                  <Option value="COMPANY">Company</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                <Select>
                  <Option value="NEW">New</Option>
                  <Option value="CONTACTED">Contacted</Option>
                  <Option value="QUALIFIED">Qualified</Option>
                  <Option value="REJECTED">Rejected</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.type !== currentValues.type}
          >
            {({ getFieldValue }) => {
              const type = getFieldValue('type');
              return type === 'COMPANY' ? (
                <Form.Item name="companyName" label="Company Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              ) : (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input type="email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="Address">
            <TextArea rows={2} />
          </Form.Item>

          <Divider>Claim Information</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="claimType" label="Claim Type">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedValue" label="Estimated Value">
                <Input type="number" prefix="$" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="claimDescription" label="Claim Description">
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dateOfLoss" label="Date of Loss">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="insurerName" label="Insurer Name">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="policyNumber" label="Policy Number">
            <Input />
          </Form.Item>

          <Divider>Lead Management</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
                <Select>
                  <Option value="LOW">Low</Option>
                  <Option value="MEDIUM">Medium</Option>
                  <Option value="HIGH">High</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source" label="Source">
                <Select allowClear>
                  <Option value="Website">Website</Option>
                  <Option value="Referral">Referral</Option>
                  <Option value="Advertisement">Advertisement</Option>
                  <Option value="Social Media">Social Media</Option>
                  <Option value="Cold Call">Cold Call</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="assignedTo" label="Assigned To">
                <Select allowClear>
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lastContactDate" label="Last Contact Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="nextFollowUpDate" label="Next Follow-up Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Convert Modal */}
      <Modal
        title="Convert Lead to Client"
        open={isConvertModalVisible}
        onOk={handleConvertConfirm}
        onCancel={() => setIsConvertModalVisible(false)}
      >
        <p>
          Are you sure you want to convert this lead to a client? This will:
        </p>
        <ul>
          <li>Create a new client record</li>
          {convertingLead?.claimType && convertingLead?.claimDescription && (
            <li>Create a new claim record</li>
          )}
          <li>Mark the lead as converted</li>
        </ul>
        <p>
          <strong>Lead:</strong> {convertingLead?.type === 'COMPANY' 
            ? convertingLead?.companyName 
            : `${convertingLead?.firstName} ${convertingLead?.lastName}`
          }
        </p>
      </Modal>
    </div>
  );
};

export default LeadsManagement; 